import { useEffect } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import { BaseDialog, PrimaryButton, notify } from '../index';
import { useAddFamilyForm, useUpdateMember, useEmployeeFamily } from './EmployeeUtils';
import AddFamilyForm from './AddFamilyForm';
import { AddFamilyDialogProps } from './types';

export default function AddFamilyDialog({
  open,
  onClose,
  employee,
  corpId,
  portal,
}: AddFamilyDialogProps) {
  const { memberData: employeeData } = useUpdateMember({
    employee,
    member: { patient: employee.patient },
    corpId,
    portal,
  });
  const { addFamilyMember, isAddingFamily } = useEmployeeFamily({ employee, portal, corpId });

  const {
    formState,
    setFormState,
    resetFormState,
    clearErrors,
    clearError,
    errors,
    handleChange,
    validate,
  } = useAddFamilyForm();

  const { memberData } = useUpdateMember({ employee, member: employee, portal, corpId });

  const handleClose = () => {
    resetFormState();
    clearErrors();
    onClose();
  };

  useEffect(() => {
    if (formState.useSameAddress && employeeData?.address && !formState.address1.trim()) {
      setFormState({
        address1: employeeData.address.address1,
        address2: employeeData.address.address2,
        city: employeeData.address.city,
        state: employeeData.address.state,
        zip: employeeData.address.zip,
      });
      clearError('address1');
      clearError('address2');
      clearError('city');
      clearError('state');
      clearError('zip');
    }
  }, [formState, employeeData]);

  function canSubmit(submit: () => Promise<void>) {
    const isValid = validate();
    if (isValid) {
      submit();
    }
  }

  function submitFamily() {
    return addFamilyMember(formState).then(() => {
      notify('Successfully added new family member', 'success');
      handleClose();
    });
  }

  return (
    <BaseDialog
      open={open}
      onClose={handleClose}
      dialogName="edit-employee-dialog"
      showCloseButton
      maxWidth="md"
      title="Add family"
    >
      <Box margin="0 auto" width="fit-content">
        <Box maxWidth={600}>
          <AddFamilyForm
            formState={formState}
            setFormState={setFormState}
            errors={errors}
            handleChange={handleChange}
            primaryMemberTags={memberData.member_tags}
          />
        </Box>
        <Stack direction="row" mt={4}>
          <PrimaryButton
            id="add-new-family-member-button"
            disabled={isAddingFamily}
            onClick={() => {
              canSubmit(submitFamily);
            }}
          >
            Add new member
          </PrimaryButton>
          <Button onClick={handleClose}>Cancel</Button>
        </Stack>
      </Box>
    </BaseDialog>
  );
}
